import { Component, Event, EventEmitter, h, Method, Prop, State } from "@stencil/core";
import { TableHeader } from "../table-header/table-header";
import { TableCell } from "../table-cell/table-cell";
import { SortDirection } from "../table-types";
import { Table } from "../table";

@Component({
    tag: "uic-table-column-editable",
    shadow: true
})
export class TableColumnEditable {
    /** defines which field of the table will be displayed in the column */
    @Prop() readonly field: string;

    /** defines the label of the table column */
    @Prop() readonly label: string;
    /** defines if the column should be a group */
    @Prop() readonly group: boolean = false;
    /** defines if the column should be sortable */
    @Prop() readonly sortable: boolean = false;
    /** defines if the checkbox should be editable */
    @Prop() readonly editable: boolean = false;

    /**
     * The minimum value.
     */
    @Prop() readonly min?: number;

    /**
     * The maximum value.
     */
    @Prop() readonly max?: number;

    /**
     * The maximum number of decimal places.
     * Set to `0` to only allow integer values.
     */
    @Prop() readonly precision: number = 0;

    /**
     * The increment size to use when using keyboard navigation.
     */
    @Prop() readonly step: number = 1;

    /**
     * The default value of the input after initialization.
     * Also used in combination with `clampOnBlur` to reset to a valid value.
     */
    @Prop() readonly defaultValue?: number;

    /**
     * If set to `true`, clamps the value to `min` and `max` after losing focus.
     * The value will be clamped to each bound only if the bound is defined.
     */
    @Prop() readonly clampOnBlur: boolean = false;

    /**
     * Defines the unit of the value
     */
    @Prop() readonly unit?: string;

    @State() initialSortDirection: SortDirection = "";

    /** make the render function public */
    @Method()
    async getColumnMethods() {
        return {
            renderCell: this.renderCell.bind(this),
            renderHeader: this.renderHeader.bind(this),
            getSortString: this.getSortString.bind(this)
        };
    }

    /**
     * triggered when a checkbox was changed
     */
    @Event({
        eventName: "uicChange"
    })
    valueChange: EventEmitter;

    private value: string | number;

    private onValueChange = (event: CustomEvent, row, field) => {
        event.stopPropagation();
        this.value = event.detail;
        row[field] = this.value;
        this.valueChange.emit({
            row,
            field: field
        });
    };

    private getSortString() {
        return this.value;
    }

    private renderHeader({ initialSortingCriterion: sortCriterion }: Table) {
        const sortData = sortCriterion?.sortProperty === this.field ? sortCriterion : undefined;
        return (
            <TableHeader
                label={this.label}
                sortable={this.sortable}
                sortField={this.field}
                group={this.group}
                testId={this.field}
                initialSortDirection={sortData?.direction ?? ""}
            />
        );
    }
    // eslint-disable-next-line
    private renderCell(row: any) {
        const { field, group, editable } = this;
        const empty = field === "";
        this.value = row[field];
        return (
            <TableCell group={group} testId={field}>
                {!empty && (
                    <uic-input-field-number
                        onUicChange={(e) => this.onValueChange(e, row, field)}
                        disabled={!editable}
                        clampOnBlur={this.clampOnBlur}
                        min={this.min}
                        max={this.max}
                        precision={this.precision}
                        step={this.step}
                        defaultValue={this.defaultValue}
                        unit={this.unit}
                        value={row[field]}
                    ></uic-input-field-number>
                )}
            </TableCell>
        );
    }
}
